import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';
import { Regex } from '@shared/enums/regex';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { CustomerService } from '@sales/modules/details/services/customer.service';
import { VoiceSubscriptionType } from '../modules/subscription/models/offerings-response.model';
import { CustomerProductSelectors } from '../states/customer-product/customer-product.selectors';
import { CustomerSelectors } from '../modules/details/states/customer.selectors';
import { CanHaveTrialReason } from '../modules/details/models/can-have-trial-response.model';
import { updateFormAfterAsyncValidation } from '@app/core/utils';

export class TrialSubscription {
  static isValid = (
    customerService: CustomerService,
    voiceProductId: string,
    store,
    onCompleteCallback: () => void = null
  ): AsyncValidatorFn => {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      const customerId = store.selectSnapshot(CustomerSelectors.customerId);

      const allVoiceProducts = store.selectSnapshot(CustomerProductSelectors.getAllVoiceProducts);
      const currentVoiceProduct = allVoiceProducts.find((x: VoiceProduct) => x.id === voiceProductId);

      const isValidPhone = RegExp(Regex.Phone).test(control.value);
      const isTrialSubscription = currentVoiceProduct.offering.subscriptionType === VoiceSubscriptionType.Trial;

      if (isTrialSubscription && isValidPhone && customerId) {
        return timer(500).pipe(
          switchMap(() => {
            return customerService.getCanHaveTrial(customerId, control.value).pipe(
              map(canHaveTrialResponse => {
                if (canHaveTrialResponse.canHaveTrial) {
                  return null;
                }

                switch (canHaveTrialResponse.reason) {
                  case CanHaveTrialReason.InRecentIceCustomerQuarantine:
                    return { isInRecentIceCustomerQuarantine: true };
                  case CanHaveTrialReason.InTrialQuarantine:
                  case CanHaveTrialReason.HasActiveTrial:
                  case CanHaveTrialReason.HasActiveSubscription:
                    return { hasUsedTrialSubscriptionOnANumber: true };
                }

                return null;
              }),
              finalize(() => {
                updateFormAfterAsyncValidation(control);

                if (onCompleteCallback) {
                  onCompleteCallback();
                }
              }),
              catchError(() => of({ apiException: true }))
            );
          })
        );
      }

      return of(null);
    };
  };
}
